import { useState, useEffect, useMemo, useRef } from "react";
import { rejectOrCb, getResParser, getCTypeHeaderVal, getErrorBody } from "./utils";
export default function useCancelableReq(fn, opts) {
    var _a = opts || {}, _b = _a.isLazy, isLazy = _b === void 0 ? false : _b, _c = _a.cancelOnUnmount, cancelOnUnmount = _c === void 0 ? true : _c, controller = _a.controller, onComplete = _a.onComplete, onFail = _a.onFail, onCancel = _a.onCancel;
    var _d = useState(!isLazy), isLoading = _d[0], setIsLoading = _d[1];
    var resRef = useRef();
    var _e = useState(), resData = _e[0], setResData = _e[1];
    var _f = useState(), error = _f[0], setError = _f[1];
    var abortController = useMemo(function () { return controller || new AbortController(); }, []);
    function setResponse(response) {
        resRef.current = response;
    }
    function handleSetError(data) {
        var errorBody = getErrorBody(data);
        // Set response object for axios
        if (data.request) {
            setResponse(data);
        }
        setError(errorBody);
        setIsLoading(false);
        onFail === null || onFail === void 0 ? void 0 : onFail(errorBody);
    }
    function handleSetResData(data) {
        setResData(data);
        setIsLoading(false);
        onComplete === null || onComplete === void 0 ? void 0 : onComplete(data);
    }
    function cancel() {
        abortController.abort();
        onCancel === null || onCancel === void 0 ? void 0 : onCancel();
    }
    function processResult(response, isMounted) {
        var contentheader = getCTypeHeaderVal(response);
        var resHandler = getResParser(contentheader);
        resRef.current = response;
        if (response['data']) {
            // Get `axios` res data
            rejectOrCb(handleSetResData, { isMounted: isMounted, data: response['data'] });
        }
        else {
            // Get `fetch` res data
            response[resHandler]()
                .then(function (data) { return rejectOrCb(response.ok ? handleSetResData : handleSetError, { isMounted: isMounted, data: data }); })
                .catch(function (error) { return rejectOrCb(handleSetError, { isMounted: isMounted, data: error }); });
        }
    }
    function makeRequest() {
        setIsLoading(true);
        fn(abortController)
            .then(function (response) { return rejectOrCb(processResult, { isMounted: true, data: response }); })
            .catch(function (error) { return rejectOrCb(handleSetError, { isMounted: true, data: error.response || error }); });
    }
    useEffect(function () {
        var isMounted = true;
        if (!isLazy) {
            fn(abortController)
                .then(function (response) { return rejectOrCb(processResult, { isMounted: isMounted, data: response }); })
                .catch(function (error) { return rejectOrCb(handleSetError, { isMounted: isMounted, data: error.response || error }); });
        }
        if (cancelOnUnmount) {
            return function () {
                isMounted = false;
                if (!abortController.signal.aborted) {
                    abortController.abort();
                }
            };
        }
        return;
    }, []);
    return {
        res: resRef.current,
        data: resData,
        error: error,
        isLoading: isLoading,
        cancel: cancel,
        makeLazyRequest: isLazy ? makeRequest : null
    };
}
